import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTrialContext } from '../context/TrialContext';
import { useTheme } from '../context/ThemeContext';
import { trackQuizProgress, trackQuizAbandoned } from '../services/analytics';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuizTakeAll = () => {
  const { quizId } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const { token } = useAuth();
  const { trialEmail } = useTrialContext();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // Track progress milestones (prevent duplicate events)
  const progressTracked = useRef({ opened: false, first: false, p25: false, p50: false, p75: false });

  const handleLeaveQuiz = () => {
    setShowLeaveModal(true);
  };

  const confirmLeave = () => {
    // Track abandonment before leaving
    if (quiz) {
      const answeredCount = Object.keys(answers).length;
      trackQuizAbandoned(quizId, quiz.questions.length, answeredCount, token ? 'premium' : 'trial');
    }
    // Navigate back appropriately based on user type
    navigate(token ? '/dashboard' : '/');
  };

  const cancelLeave = () => {
    setShowLeaveModal(false);
  };

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);
  
  // Track quiz opened when quiz loads
  useEffect(() => {
    if (quiz && !progressTracked.current.opened) {
      progressTracked.current.opened = true;
      trackQuizProgress.opened(quizId, quiz.questions?.length || 0);
    }
  }, [quiz, quizId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quiz/${quizId}`);
      setQuiz(response.data);
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
      alert('Failed to load quiz');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);
    
    // Track progress milestones
    if (quiz) {
      const answeredCount = Object.keys(newAnswers).length;
      const totalQuestions = quiz.questions.length;
      
      // Track first answer
      if (answeredCount === 1 && !progressTracked.current.first) {
        progressTracked.current.first = true;
        trackQuizProgress.firstAnswer(quizId, totalQuestions);
      }
      
      // Track percentage milestones
      const percentage = (answeredCount / totalQuestions) * 100;
      if (percentage >= 25 && percentage < 50 && !progressTracked.current.p25) {
        progressTracked.current.p25 = true;
        trackQuizProgress.progress(quizId, totalQuestions, answeredCount);
      } else if (percentage >= 50 && percentage < 75 && !progressTracked.current.p50) {
        progressTracked.current.p50 = true;
        trackQuizProgress.progress(quizId, totalQuestions, answeredCount);
      } else if (percentage >= 75 && percentage < 100 && !progressTracked.current.p75) {
        progressTracked.current.p75 = true;
        trackQuizProgress.progress(quizId, totalQuestions, answeredCount);
      }
    }
  };

  const handleSubmitQuiz = async () => {
    // Check if all questions are answered
    const unanswered = quiz.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      const confirm = window.confirm(
        `You have ${unanswered.length} unanswered question(s). Do you want to submit anyway?`
      );
      if (!confirm) return;
    }

    setSubmitting(true);
    
    try {
      const submission = {
        quiz_id: quizId,
        answers: Object.entries(answers).map(([question_id, user_answer]) => ({
          question_id,
          user_answer
        }))
      };

      // Build headers - support both authenticated and trial users
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (trialEmail) {
        headers['X-Trial-Email'] = trialEmail;
      }

      const response = await axios.post(
        `${API_URL}/api/quiz/submit`,
        submission,
        { headers }
      );

      // Pass both answers and result to the results page
      navigate(`/quiz/result/${quizId}`, { 
        state: { 
          answers, 
          result: response.data,
          isTrialUser: !token && !!trialEmail
        } 
      });
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      alert(error.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (!quiz) return null;

  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / quiz.questions.length) * 100;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`shadow-sm border-b sticky top-0 z-10 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLeaveQuiz}
                className={`flex items-center transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Back</span>
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{quiz.title}</h1>
              <span className={`text-sm capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Difficulty: {quiz.difficulty}
              </span>
            </div>
            <div className="w-40"></div> {/* Spacer for centering */}
          </div>
          
          {/* Progress Bar */}
          <div className="relative">
            <div className={`flex justify-between text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <span>{answeredCount} of {quiz.questions.length} answered</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* All Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {quiz.questions.map((question, idx) => (
            <div
              key={question.id}
              className={`rounded-xl shadow-sm p-8 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            >
              {/* Question Number and Type Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white font-bold text-sm">
                    {idx + 1}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`}>
                    {question.type.replace('_', ' ')}
                  </span>
                </div>
                {answers[question.id] && (
                  <span className="text-sm text-green-600 dark:text-green-400 font-medium flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Answered
                  </span>
                )}
              </div>

              {/* Question Text */}
              <h2 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {question.question}
              </h2>

              {/* Answer Options */}
              {question.type === 'fill_blank' || question.type === 'short_answer' ? (
                <input
                  type="text"
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswer(question.id, e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  placeholder="Type your answer..."
                />
              ) : (
                <div className="space-y-3">
                  {question.options?.map((option, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => handleAnswer(question.id, option)}
                      className={`w-full text-left p-4 border-2 rounded-lg transition-all ${
                        answers[question.id] === option
                          ? isDarkMode 
                            ? 'border-indigo-500 bg-indigo-900/30' 
                            : 'border-indigo-600 bg-indigo-50'
                          : isDarkMode 
                            ? 'border-gray-600 hover:border-gray-500' 
                            : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                          answers[question.id] === option
                            ? 'border-indigo-600 bg-indigo-600'
                            : isDarkMode ? 'border-gray-500' : 'border-gray-400'
                        }`}>
                          {answers[question.id] === option && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className={isDarkMode ? 'text-gray-200' : 'text-gray-900'}>{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className={`mt-8 rounded-xl shadow-sm p-6 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {answeredCount === quiz.questions.length 
                  ? '✓ All questions answered!' 
                  : `${quiz.questions.length - answeredCount} question(s) remaining`}
              </p>
            </div>
            <button
              onClick={handleSubmitQuiz}
              disabled={submitting || answeredCount === 0}
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </button>
          </div>
        </div>
      </div>

      {/* Leave Quiz Confirmation Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={cancelLeave}>
          <div className={`rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-yellow-900/50' : 'bg-yellow-100'}`}>
                <svg className={`w-8 h-8 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Leave Quiz?</h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Are you sure you want to leave this quiz? Your answers will not be saved.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelLeave}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLeave}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-all"
                >
                  Leave Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizTakeAll;
